import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortDown,
  faSortUp,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { type LinkProps } from "@tanstack/react-router";
import * as React from "react";
import { useEffect } from "react";
import { Sortable } from "../Sortable/Sortable";
import { SortableItem, SortableItemHandle } from "../Sortable/SortableItem";
import { Checkbox } from "../ui/checkbox";
import { GroupedTableTopbar } from "./GroupedTableTopbar";
import { useColumnSorting, useGroupedTableSearchAndFilters } from "./hooks";
import { sortGroupedData } from "./lib";

export interface GroupedTableColumn<
  TSource extends object,
  K extends keyof TSource = keyof TSource,
> {
  key: K;
  label: string;
  className?: string;
}

export interface GroupedTableGroup<
  TSource extends object,
  TGroupMeta = undefined,
> {
  groupId: string;
  label: string;
  items: TSource[];
  meta?: TGroupMeta;
  actions?: React.ReactNode;
  sortable?: boolean;
}

interface GroupedTableProps<TSource extends object, TGroupMeta = undefined> {
  data: GroupedTableGroup<TSource, TGroupMeta>[];
  columns: GroupedTableColumn<TSource, keyof TSource>[];
  itemIdKey?: keyof TSource;
  rowLink?: (row: TSource) => LinkProps;
  renderActions?: (args: {
    row: TSource;
    rowIndex: number;
    groupLabel: string;
    groupId: string;
  }) => React.ReactNode;
  /**
   * Object whose keys are column keys and values are render functions for the cell value.
   * Signature: (value, row) => React.ReactNode
   */
  renderCell?: {
    [K in keyof TSource]?: (
      value: TSource[K],
      row: TSource,
      groupMeta: TGroupMeta,
    ) => React.ReactNode;
  };
  className?: string;
  groupHeaderClassName?: string;
  rowClassName?: string;
  emptyMessage?: string;
  ignoreEmptyMessage?: boolean;
  onSort?: (groupId: string, items: TSource[]) => void;
  checkedActions?: (selectedItems: Map<string, TSource>) => React.ReactNode;
  isSearchable?: boolean;
  searchableFields?: (keyof TSource)[];
  /** Enable column sorting functionality (independent of search/filters) */
  isSortable?: boolean;
  /** Show the group header row even when there's only a single group */
  showSingleGroupHeader?: boolean;
}

type ItemWithId<TSource> = {
  id: string;
  data: TSource;
};

export function GroupedTable<TSource extends object, TGroupMeta = undefined>({
  data,
  columns,
  itemIdKey,
  rowLink,
  renderActions,
  renderCell,
  className,
  groupHeaderClassName,
  rowClassName,
  emptyMessage = "No data available",
  ignoreEmptyMessage = false,
  onSort,
  checkedActions,
  isSearchable = false,
  isSortable = false,
  searchableFields,
  showSingleGroupHeader = false,
}: GroupedTableProps<TSource, TGroupMeta>) {
  const [selectedItems, setSelectedItems] = React.useState<
    Map<string, TSource>
  >(new Map());

  // Use separate hooks for search/filters and sorting
  const searchAndFilters = useGroupedTableSearchAndFilters(
    data,
    columns,
    searchableFields,
  );
  const sorting = useColumnSorting(data, columns);

  // Build display data by applying filters and sorting separately
  const displayData = React.useMemo(() => {
    let result = data;

    // Apply search and group filters if enabled
    if (isSearchable) {
      result = searchAndFilters.filteredData;
    }

    // Apply sorting if enabled
    if (isSortable && sorting.sortState.column) {
      // Flatten groups when actively sorting
      const flattenGroups = sorting.isActivelySorting();
      result = sortGroupedData(result, sorting.sortState, flattenGroups);
    }

    return result;
  }, [
    data,
    isSearchable,
    searchAndFilters.filteredData,
    isSortable,
    sorting.sortState,
    sorting.isActivelySorting,
  ]);

  const getId = (params: { item: TSource; groupId: string; index: number }) => {
    const { item, groupId, index } = params;

    if (itemIdKey) {
      return String(item[itemIdKey]);
    }

    if ("id" in item) {
      return String((item as unknown as { id: string }).id);
    }

    if ("uuid" in item) {
      return String((item as unknown as { uuid: string }).uuid);
    }

    return `${String(groupId)}-${String(index)}`;
  };

  useEffect(() => {
    // Deselect items that are no longer in the displayed data
    const validIds = new Set<string>();
    displayData.forEach((group) =>
      group.items.forEach((item, index) => {
        const id = getId({ item, groupId: group.groupId, index });
        validIds.add(id);
      }),
    );
    setSelectedItems((prev) => {
      const newMap = new Map<string, TSource>();
      prev.forEach((value, key) => {
        if (validIds.has(key)) {
          newMap.set(key, value);
        }
      });
      return newMap;
    });
  }, [data]);

  const hasData = displayData.some((group) => group.items.length > 0);
  const hasActionColumn = renderActions ?? data.some((group) => group.actions);

  // Define helper functions that are needed in empty state
  const handleSelect = (item: ItemWithId<TSource>) => {
    setSelectedItems((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(item.id)) {
        newMap.delete(item.id);
      } else {
        newMap.set(item.id, item.data);
      }
      return newMap;
    });
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      // Deselect all
      setSelectedItems(new Map());
    } else {
      // Select all (from filtered data)
      const newMap = new Map<string, TSource>();
      displayData.forEach((group) => {
        group.items.forEach((item, index) => {
          const id = getId({ item, groupId: group.groupId, index });
          newMap.set(id, item);
        });
      });
      setSelectedItems(newMap);
    }
  };

  const isAllSelected =
    selectedItems.size > 0 &&
    selectedItems.size ===
      displayData.reduce((acc, group) => acc + group.items.length, 0);

  if (!hasData && !ignoreEmptyMessage && !searchAndFilters.searchText) {
    return (
      <div className="text-muted-foreground flex items-center justify-center py-8">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      <GroupedTableTopbar
        selectedItems={selectedItems}
        isSearchable={isSearchable}
        checkedActions={checkedActions}
        onSelectAll={handleSelectAll}
        isAllSelected={isAllSelected}
        searchAndFilters={isSearchable ? searchAndFilters : undefined}
      />
      <Table>
        <TableHeader>
          <TableRow className="border-none">
            {checkedActions && (
              <TableHead className="w-8">
                {/* Empty header for checkbox column */}
              </TableHead>
            )}
            {columns.map((column, idx) => {
              const isColumnSortable = isSortable
                ? sorting.isSortable(column.key)
                : false;
              const isCurrentSort = isSortable
                ? sorting.sortState.column === column.key
                : false;

              const getSortIcon = () => {
                if (!isColumnSortable) return null;
                if (!isCurrentSort)
                  return (
                    <FontAwesomeIcon
                      icon={faSort}
                      className="text-muted-foreground ml-1"
                    />
                  );
                if (sorting.sortState.direction === "asc")
                  return <FontAwesomeIcon icon={faSortUp} className="ml-1" />;
                if (sorting.sortState.direction === "desc")
                  return <FontAwesomeIcon icon={faSortDown} className="ml-1" />;
                return (
                  <FontAwesomeIcon
                    icon={faSort}
                    className="text-muted-foreground ml-1"
                  />
                );
              };

              return (
                <TableHead
                  key={idx}
                  className={cn(
                    "text-accent text-xs font-semibold tracking-wide capitalize",
                    column.className,
                    isColumnSortable && "hover:text-foreground cursor-pointer",
                  )}
                  onClick={
                    isColumnSortable
                      ? () => sorting.toggleSort(column.key)
                      : undefined
                  }
                >
                  <div className="flex items-center">
                    {column.label}
                    {getSortIcon()}
                  </div>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayData.map((group, groupIndex) => {
            const itemsWithId: ItemWithId<TSource>[] = group.items.map(
              (item, index) => ({
                id: getId({ item, groupId: group.groupId, index }),
                data: item,
              }),
            );
            return (
              <React.Fragment key={groupIndex}>
                {(displayData.length > 1 || showSingleGroupHeader) &&
                  !sorting.isActivelySorting() && (
                    <TableRow
                      className={cn("border-none", groupHeaderClassName)}
                    >
                      {checkedActions && (
                        <TableCell>
                          {/* Empty cell for checkbox column */}
                        </TableCell>
                      )}
                      <TableCell
                        className={cn(
                          "text-accent font-md text-xs tracking-wide capitalize",
                          columns[0]?.className,
                          groupHeaderClassName,
                        )}
                      >
                        {group.label}
                      </TableCell>
                      {(() => {
                        const emptyColumns = [];
                        for (let idx = 0; idx < columns.length - 1; idx++) {
                          emptyColumns.push(
                            <TableCell
                              key={idx}
                              className={cn(
                                "text-accent font-md text-xs tracking-wide",
                                columns[idx]?.className,
                              )}
                            >
                              {""}
                            </TableCell>,
                          );
                        }
                        return emptyColumns;
                      })()}
                      {hasActionColumn && (
                        <TableCell>{group.actions}</TableCell>
                      )}
                    </TableRow>
                  )}
                <Sortable
                  disabled={
                    !group.sortable || !onSort || sorting.isActivelySorting()
                  }
                  items={itemsWithId}
                  onChange={(items) =>
                    onSort?.(
                      group.groupId,
                      items.map((item) => item.data),
                    )
                  }
                  renderItem={({ item, index: rowIndex, isDragging }) => {
                    const { data: row, id } = item;
                    return (
                      <SortableItem
                        id={id}
                        key={id}
                        as={TableRow}
                        className={cn(
                          "group/row overflow-hidden transition-colors",
                          {
                            "border-b-transparent":
                              rowIndex === group.items.length - 1,
                            "border-t-0": rowIndex === 0,
                          },
                          rowClassName,
                        )}
                        link={rowLink ? rowLink(row) : undefined}
                      >
                        {checkedActions && (
                          <TableCell
                            className="cursor-default"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                          >
                            <Checkbox
                              checked={selectedItems.has(item.id)}
                              onCheckedChange={() => {
                                handleSelect(item);
                              }}
                              aria-label={
                                selectedItems.has(item.id)
                                  ? "Deselect"
                                  : "Select"
                              }
                              title={
                                selectedItems.has(item.id)
                                  ? "Deselect"
                                  : "Select"
                              }
                            />
                          </TableCell>
                        )}
                        {columns.map((column) => (
                          <TableCell
                            key={String(column.key)}
                            className={cn(column.className, {
                              "bg-accent-3": isDragging,
                            })}
                          >
                            {renderCell && renderCell[column.key]
                              ? renderCell[column.key]!(
                                  row[column.key],
                                  row,
                                  group.meta!,
                                )
                              : String(row[column.key])}
                          </TableCell>
                        ))}
                        {hasActionColumn && (
                          <TableCell
                            className={cn("cursor-default", {
                              "bg-accent-3": isDragging,
                            })}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {renderActions && (
                                <div className="flex items-center gap-1">
                                  {renderActions({
                                    row,
                                    rowIndex,
                                    groupLabel: group.label,
                                    groupId: group.groupId,
                                  })}
                                </div>
                              )}
                              {group.sortable &&
                                !sorting.isActivelySorting() && (
                                  <SortableItemHandle />
                                )}
                            </div>
                          </TableCell>
                        )}
                      </SortableItem>
                    );
                  }}
                ></Sortable>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
